import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { NumberParam, StringParam, useQueryParam, useQueryParams } from 'use-query-params'
import dedicatedPreorderService from '../../../api/dedicatedPreorderService'
import { Preorder, PreorderListItem, PreorderStatus } from '../../../api/dedicatedPreorderTypes'
import { Button, TopProgress } from '../../../components'
import Pages from '../../../components/pagination'
import StyledIcon from '../../../components/styled-icon'
import { useAuth } from '../../../context/user.context'
import { useUi } from '../../../context/ui.context'
import { icons } from '../../../icons'
import { isDedicatedServiceInactiveError } from '../../../utils/dedicated-service'
import { apiErrorMessage } from '../create-page/model'
import { diskTypeLabel, formatCapacity, formatShortDate, preorderHardware, stateColors } from '../list/format'
import { isPreorderHistory, loadPreorders } from './list'
import {
  Alert, Cell, Chevron, CloseButton, Confirm, ConfirmActions, ConfirmIcon, ConfirmTitle, Date as DateText, Description,
  Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeading, DrawerTitle, Empty, Eyebrow, Fact, Facts, Footer, Muted,
  Name, Overlay, Pill, Row, Rows, Section, SectionHeader, SectionText, SectionTitle, Step, Steps, Tab, TabCount, Tabs,
  TabsNote, TabsRow, Text, Timeline, TimelineItem, TimelineLabel,
} from './styles'

export const canCancelPreorder = (status: string) => status === 'submitted' || status === 'provisioning'

const PAGE_SIZE = 5
const order: PreorderStatus[] = ['submitted', 'provisioning', 'released']
const grey = { dot: '#DBDBDB', text: '#858A94', line: '#E8E8E8', halo: '#DFEFFF' }

const pills: Record<PreorderStatus, { bg: string; color: string }> = {
  submitted: { bg: '#DFEFFF', color: '#0F7BE6' },
  provisioning: { bg: '#FDF5E1', color: '#8A6A10' },
  released: { bg: '#E7F4EE', color: stateColors.ok },
  cancelled: { bg: '#F3F1F1', color: '#6C6C6C' },
}

// Three-step progress: done steps are green, the current one is blue with a halo.
const progressSteps = (status: PreorderStatus) => {
  const index = order.indexOf(status)
  return order.map((step, i) => ({
    step,
    dot: i < index || (i === index && step === 'released') ? stateColors.ok : i === index ? stateColors.blue : grey.dot,
    halo: i === index && step !== 'released' ? grey.halo : 'transparent',
    active: i <= index,
    line: i < order.length - 1 ? (i < index ? stateColors.ok : grey.line) : undefined,
  }))
}

type Props = { onActiveCount?: (count: number) => void }

export default function Preorders({ onActiveCount }: Props) {
  const [t, i18n] = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { viewAlert, setViewModalComplete, setActiveAnimationComplete } = useUi()
  const [query, setQuery] = useQueryParams({ preorder_page: NumberParam, preorder_history_page: NumberParam, preorder_tab: StringParam })
  const [selectedId, setSelectedId] = useQueryParam('preorder', StringParam)
  const [data, setData] = useState<PreorderListItem[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState<Preorder>()
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const scrolledToSection = useRef(false)
  const request = useRef(0)
  const detailRequest = useRef(0)
  const cancelPending = useRef(false)
  const stepLabel = (status: PreorderStatus) => t(`DEDICATED_PREORDER_STEP_${status.toUpperCase()}`)
  const date = (value?: string | null) => formatShortDate(value, i18n?.language)

  const refresh = useCallback(async () => {
    const id = ++request.current
    try {
      const items = await loadPreorders(() => request.current === id)
      if (!items || request.current !== id) return
      setData(items)
      setError('')
    } catch (err) {
      if (request.current !== id) return
      if (isDedicatedServiceInactiveError(err)) navigate('/bare-metal', { replace: true })
      else setError(apiErrorMessage(err, t('ERROR_OCCURRED')))
    } finally {
      if (request.current === id) setLoading(false)
    }
  }, [navigate, t])

  useEffect(() => {
    setLoading(true)
    setData(undefined)
    void refresh()
    const interval = setInterval(() => { void refresh() }, 15000)
    const invalidateRequest = () => { request.current++ }
    return () => { invalidateRequest(); clearInterval(interval) }
  }, [refresh])

  const loadDetail = useCallback(async () => {
    const id = ++detailRequest.current
    if (!selectedId) { setDetail(undefined); return }
    setDetailLoading(true)
    setDetailError('')
    try {
      const response = await dedicatedPreorderService.getById(selectedId)
      if (detailRequest.current === id) setDetail(response.data)
    } catch (err) {
      if (detailRequest.current === id) setDetailError(apiErrorMessage(err, t('ERROR_OCCURRED')))
    } finally {
      if (detailRequest.current === id) setDetailLoading(false)
    }
  }, [selectedId, t])

  useEffect(() => {
    setDetail(undefined)
    setConfirmCancel(false)
    void loadDetail()
    const invalidateRequest = () => { detailRequest.current++ }
    return invalidateRequest
  }, [loadDetail])

  const active = (data || []).filter(item => !isPreorderHistory(item))
  const history = (data || []).filter(isPreorderHistory)
  const tab = query.preorder_tab === 'history' ? 'history' : 'active'

  useEffect(() => {
    if (data) onActiveCount?.(active.length)
  }, [active.length, data, onActiveCount])

  useEffect(() => {
    if (!scrolledToSection.current && data && (selectedId || new URLSearchParams(location.search).get('view') === 'preorders')) {
      scrolledToSection.current = true
      sectionRef.current?.scrollIntoView?.({ block: 'start' })
    }
  }, [data, selectedId, location.search])

  const closeDetail = useCallback(() => {
    if (!cancelPending.current) setSelectedId(undefined)
  }, [setSelectedId])

  useEffect(() => {
    if (!selectedId) return
    closeRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (confirmCancel) setConfirmCancel(false)
      else closeDetail()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [closeDetail, confirmCancel, selectedId])

  const cancel = async () => {
    if (!detail || cancelPending.current) return
    const id = detailRequest.current
    cancelPending.current = true
    setCancelling(true)
    setDetailError('')
    try {
      const response = await dedicatedPreorderService.cancel(detail.id)
      if (id !== detailRequest.current) return
      setDetail(response.data)
      setConfirmCancel(false)
      viewAlert({ severity: 'success', message: String(t('DEDICATED_PREORDER_CANCELLED')) })
      void refresh()
    } catch (err) {
      if (id === detailRequest.current) {
        setConfirmCancel(false)
        setDetailError(apiErrorMessage(err, t('ERROR_OCCURRED')))
      }
    } finally {
      cancelPending.current = false
      if (id === detailRequest.current) setCancelling(false)
    }
  }

  const create = () => {
    if (!user.user.active_client) navigate('/complete-registration')
    else if (user.requires_email_verification) { setViewModalComplete(true); setActiveAnimationComplete(true) }
    else navigate('/bare-metal/create?mode=preorder')
  }

  const rows = tab === 'active' ? active : history
  const rawPage = tab === 'active' ? query.preorder_page : query.preorder_history_page
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const currentPage = Math.max(1, Math.min(Number.isFinite(rawPage) ? Math.trunc(rawPage!) : 1, pageCount))
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const setPage = (value: number) => setQuery(tab === 'active' ? { preorder_page: value } : { preorder_history_page: value })

  const timeline = (item: Preorder) => {
    if (item.status === 'cancelled') {
      return [
        { label: stepLabel('submitted'), date: date(item.submitted_at || item.created_at), dot: stateColors.ok, halo: 'transparent', color: undefined, line: grey.line },
        { label: stepLabel('cancelled'), date: date(item.cancelled_at), dot: stateColors.mute, halo: '#F3F1F1', color: undefined, line: undefined },
      ]
    }
    const dates = [item.submitted_at || item.created_at, item.provisioning_at, item.released_at]
    const index = order.indexOf(item.status)
    return progressSteps(item.status).map((step, i) => ({
      label: step.step === 'released' ? t('DEDICATED_PREORDER_STEP_ISSUED') : stepLabel(step.step),
      date: date(dates[i]) || String(t(i === index + 1 ? 'DEDICATED_PREORDER_UP_NEXT' : 'DEDICATED_PREORDER_PENDING')),
      dot: step.dot, halo: step.halo, color: step.active ? undefined : grey.text, line: step.line,
    }))
  }

  const facts = (item: Preorder) => {
    const disks = new Map<string, number>()
    item.disks.forEach(({ disk }) => {
      const label = `${disk.name || disk.model} · ${formatCapacity(disk.size_gb, 'GB')} ${diskTypeLabel(disk.disk_type)}`.trim()
      disks.set(label, (disks.get(label) || 0) + 1)
    })
    const ramTotal = formatCapacity(item.ram.size_mb * item.ram_count, 'MB', true)
    return [
      { key: t('HOSTNAME'), lines: [item.hostname] },
      { key: t('DEDICATED_PROCESSOR'), lines: [`${item.cpu_count} × ${item.cpu.name || item.cpu.model}`] },
      { key: t('DEDICATED_MEMORY'), lines: [item.ram_count > 1 ? `${ramTotal} (${item.ram_count} × ${formatCapacity(item.ram.size_mb, 'MB', true)})` : ramTotal] },
      { key: t('STORAGE'), lines: disks.size ? Array.from(disks, ([label, count]) => `${count} × ${label}`) : ['—'] },
      ...(item.comment ? [{ key: t('DEDICATED_REQUIREMENTS'), lines: [item.comment] }] : []),
    ]
  }

  const drawer = selectedId ? createPortal(<>
    <Overlay onClick={closeDetail} />
    <Drawer role="dialog" aria-modal="true" aria-labelledby="dedicated-preorder-title">
      <DrawerHeader>
        <DrawerHeading>
          <Eyebrow>{t('DEDICATED_PREORDER')}</Eyebrow>
          <DrawerTitle id="dedicated-preorder-title">{detail?.name || detail?.hostname || t('DEDICATED_PREORDER')}</DrawerTitle>
          {detail && <Pill $bg={(pills[detail.status] ?? pills.cancelled).bg} $color={(pills[detail.status] ?? pills.cancelled).color}>
            {detail.status === 'released' ? t('DEDICATED_PREORDER_STEP_ISSUED') : stepLabel(detail.status)}
          </Pill>}
        </DrawerHeading>
        <CloseButton ref={closeRef} type="button" aria-label={String(t('CLOSE'))} onClick={closeDetail} disabled={cancelling}>
          <StyledIcon src={icons.CloseIcon} />
        </CloseButton>
      </DrawerHeader>
      <DrawerBody>
        {detailLoading && !detail && <Muted>{t('LOADING')}</Muted>}
        {detailError && <Alert role="alert" style={{ margin: 0 }}>
          <span>{detailError}</span>
          {!detail && <Button variant="stroke" size="small" title={t('REFRESH_LIST')} onClick={loadDetail} loading={detailLoading} />}
        </Alert>}
        {detail && <>
          <Timeline aria-label={String(t('STATUS'))}>
            {timeline(detail).map(item => (
              <TimelineItem key={item.label} $dot={item.dot} $halo={item.halo} $line={item.line}>
                <TimelineLabel $color={item.color || 'inherit'}>{item.label}</TimelineLabel>
                <Muted>{item.date}</Muted>
              </TimelineItem>
            ))}
          </Timeline>
          <Facts>
            {facts(detail).map(fact => (
              <Fact key={fact.key}>
                <dt>{fact.key}</dt>
                {fact.lines.map(line => <dd key={line}>{line}</dd>)}
              </Fact>
            ))}
          </Facts>
        </>}
      </DrawerBody>
      <DrawerFooter>
        {detail?.issued_server?.id && <Button variant="primary" size="display" title={t('DEDICATED_OPEN_SERVER')}
          onClick={() => navigate(`/bare-metal/${detail.issued_server!.id}`)} />}
        {detail && canCancelPreorder(detail.status) && <Button variant="stroke" size="display" isDelete
          title={t('DEDICATED_CANCEL_PREORDER')} onClick={() => setConfirmCancel(true)} disabled={cancelling} />}
        <Button variant="stroke" size="display" title={t('CLOSE')} onClick={closeDetail} disabled={cancelling} />
      </DrawerFooter>
    </Drawer>
    {confirmCancel && detail && <>
      <Overlay $z={1002} onClick={() => !cancelling && setConfirmCancel(false)} />
      <Confirm role="alertdialog" aria-modal="true" aria-labelledby="dedicated-preorder-cancel-title" aria-describedby="dedicated-preorder-cancel-body">
        <ConfirmIcon aria-hidden="true"><StyledIcon src={icons.DeleteIcon} /></ConfirmIcon>
        <ConfirmTitle id="dedicated-preorder-cancel-title">{t('DEDICATED_CANCEL_PREORDER_QUESTION', { name: detail.name || detail.hostname })}</ConfirmTitle>
        <Muted id="dedicated-preorder-cancel-body" style={{ fontSize: 15, lineHeight: '20px' }}>{t('DEDICATED_CANCEL_PREORDER_BODY')}</Muted>
        <ConfirmActions>
          <Button variant="stroke" size="display" title={t('DEDICATED_KEEP_PREORDER')} onClick={() => setConfirmCancel(false)}
            disabled={cancelling} customStyles={{ width: '100%' }} />
          <Button variant="primary" size="display" isDelete title={t('DEDICATED_CANCEL_PREORDER')} onClick={cancel}
            loading={cancelling} customStyles={{ width: '100%' }} />
        </ConfirmActions>
      </Confirm>
    </>}
  </>, document.body) : null

  return <Section id="preorders" ref={sectionRef} aria-labelledby="dedicated-preorders-title">
    <TopProgress loading={loading || detailLoading} />
    <SectionHeader>
      <SectionText>
        <SectionTitle id="dedicated-preorders-title">{t('DEDICATED_PREORDERS')}</SectionTitle>
        <Description>{t('DEDICATED_PREORDERS_DESCRIPTION')}</Description>
      </SectionText>
      <Button variant="stroke" size="mid" title={t('DEDICATED_NEW_PREORDER')} onClick={create} />
    </SectionHeader>
    <TabsRow>
      <Tabs role="tablist" aria-label={String(t('DEDICATED_PREORDERS'))}>
        {([['active', 'DEDICATED_ACTIVE_PREORDERS', active.length], ['history', 'DEDICATED_PREORDER_HISTORY', history.length]] as const).map(([key, label, count]) => (
          <Tab key={key} type="button" role="tab" aria-selected={tab === key} $active={tab === key}
            onClick={() => setQuery({ preorder_tab: key === 'active' ? undefined : key })}>
            {t(label)}
            {data && <TabCount $active={tab === key}>{count}</TabCount>}
          </Tab>
        ))}
      </Tabs>
      <TabsNote>{t('DEDICATED_PREORDERS_SORT_NOTE')}</TabsNote>
    </TabsRow>
    {error && <Alert role="alert">
      <span>{error}</span>
      <Button variant="stroke" size="small" title={t('REFRESH_LIST')} onClick={refresh} />
    </Alert>}
    <Rows role="tabpanel" aria-label={String(t(tab === 'active' ? 'DEDICATED_ACTIVE_PREORDERS' : 'DEDICATED_PREORDER_HISTORY'))}>
      {loading ? <Empty>{t('LOADING')}</Empty>
        : !pageRows.length ? <Empty>{t(tab === 'history' ? 'DEDICATED_NO_PREORDER_HISTORY' : data?.length ? 'DEDICATED_NO_ACTIVE_PREORDERS' : 'DEDICATED_NO_PREORDERS')}</Empty>
        : pageRows.map(item => {
          const hardware = preorderHardware(item.hardware)
          return <Row key={item.id} type="button" $active={selectedId === item.id} onClick={() => setSelectedId(item.id)}
            aria-label={`${item.name || item.hostname}, ${item.status === 'released' ? t('DEDICATED_PREORDER_STEP_ISSUED') : stepLabel(item.status)}`}>
            <Cell $basis="200px">
              <Name>{item.name || item.hostname}</Name>
              {item.name && <Muted>{item.hostname}</Muted>}
            </Cell>
            <Cell $basis="240px">
              <Text>{hardware.cpu} · {hardware.ram}</Text>
              <Muted>{hardware.disk}</Muted>
            </Cell>
            <Steps aria-hidden="true">
              {item.status === 'cancelled'
                ? <Step $dot={stateColors.mute} $halo="#F3F1F1" $color="#6C6C6C">{stepLabel('cancelled')}</Step>
                : progressSteps(item.status).map(step => (
                  <Step key={step.step} $dot={step.dot} $halo={step.halo} $color={step.active ? 'inherit' : grey.text} $line={step.line}>
                    {stepLabel(step.step)}
                  </Step>
                ))}
            </Steps>
            <DateText>{date(item.submitted_at || item.created_at)}</DateText>
            <Chevron aria-hidden="true"><StyledIcon src={icons.Chevron} /></Chevron>
          </Row>
        })}
    </Rows>
    {!loading && rows.length > 0 && <Footer>
      <span>{t('DEDICATED_SHOWN_OF', { shown: pageRows.length, count: rows.length })}</span>
      {rows.length > PAGE_SIZE && <Pages changePage={() => undefined} customSetPage={setPage}
        currentPage={currentPage} totalCount={rows.length} pageSize={PAGE_SIZE} siblingCount={1} />}
    </Footer>}
    {drawer}
  </Section>
}
